import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type CarrierAccountRow = {
  id: string;
  provider: 'FEDEX' | 'UPS';
  environment: 'SANDBOX' | 'PRODUCTION';
  label: string | null;
  accountNumber: string | null;
  shipperNumber: string | null;
  meterNumber: string | null;
  isEnabled: boolean;
};

export default function CarrierAccountsSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const canEdit =
    auth.type === 'ADMIN' || (auth.permissions || []).includes('CARRIER_ACCOUNT:UPDATE');
  const [rows, setRows] = useState<CarrierAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    provider: 'FEDEX' as 'FEDEX' | 'UPS',
    environment: 'SANDBOX' as 'SANDBOX' | 'PRODUCTION',
    label: '',
    clientId: '',
    clientSecret: '',
    accountNumber: '',
    shipperNumber: '',
    meterNumber: '',
    isEnabled: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/accounts`, {
        headers: { 'x-department-slug': '' },
      });
      if (!res.ok) throw new Error('load');
      setRows((await res.json()) as CarrierAccountRow[]);
    } catch {
      setError(t('backoffice.carrier_accounts_load_error', { defaultValue: 'Failed to load carrier accounts' }));
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-department-slug': '' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('save');
      setForm((f) => ({ ...f, clientId: '', clientSecret: '' }));
      await load();
    } catch {
      setError(t('backoffice.carrier_accounts_save_error', { defaultValue: 'Failed to save carrier account' }));
    } finally {
      setSaving(false);
    }
  };

  const removeCarrier = async (row: CarrierAccountRow) => {
    if (!canEdit) return;
    const confirmDelete = window.confirm(
      t('backoffice.carrier_accounts_delete_confirm', {
        defaultValue: 'Delete {{provider}} ({{environment}}) carrier account?',
        provider: row.provider,
        environment: row.environment,
      }),
    );
    if (!confirmDelete) return;
    setDeletingId(row.id);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/accounts/${row.id}`, {
        method: 'DELETE',
        headers: { 'x-department-slug': '' },
      });
      if (!res.ok) throw new Error('delete');
      await load();
    } catch {
      setError(
        t('backoffice.carrier_accounts_delete_error', {
          defaultValue: 'Failed to delete carrier account',
        }),
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <header className="workspace-page-header">
          <div>
            <h1 className="workspace-page-title">
              {t('backoffice.carrier_accounts_title', { defaultValue: 'Carrier accounts' })}
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              {t('backoffice.carrier_accounts_subtitle', {
                defaultValue: 'Configure FedEx and UPS API credentials (sandbox or production).',
              })}
            </p>
            <p className="mt-2 text-xs text-slate-600">
              {t('backoffice.carrier_accounts_ups_hint', {
                defaultValue:
                  'UPS: create an app at developer.ups.com, enable Rating + Shipping + Tracking, and use your sandbox shipper account number (6 characters, e.g. from the UPS test credentials page).',
              })}
            </p>
          </div>
        </header>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {loading ? (
          <p className="text-sm text-slate-600">{t('common.loading')}</p>
        ) : (
          <section className="workspace-panel space-y-3">
            <h2 className="text-base font-semibold text-slate-900">
              {t('backoffice.carrier_accounts_configured', { defaultValue: 'Configured accounts' })}
            </h2>
            {rows.length === 0 ? (
              <p className="text-sm text-slate-600">No carrier accounts yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {rows.map((row) => (
                  <li key={row.id} className="border border-slate-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0">
                        <strong>{row.provider}</strong> ({row.environment})
                        {row.label ? ` — ${row.label}` : ''}
                        {row.accountNumber ? ` · Acct ${row.accountNumber}` : ''}
                        {row.shipperNumber ? ` · Shipper ${row.shipperNumber}` : ''}
                        {!row.isEnabled ? ' · disabled' : ''}
                      </p>
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() => void removeCarrier(row)}
                          disabled={deletingId === row.id}
                          className="shrink-0 border border-red-700 px-2 py-1 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingId === row.id
                            ? t('common.deleting', { defaultValue: 'Deleting…' })
                            : t('common.delete', { defaultValue: 'Delete' })}
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {canEdit ? (
          <section className="workspace-panel space-y-3">
            <h2 className="text-base font-semibold text-slate-900">Add / update credentials</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm">
                Provider
                <select
                  className="border border-slate-300 px-2 py-2"
                  value={form.provider}
                  onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value as 'FEDEX' | 'UPS' }))}
                >
                  <option value="FEDEX">FedEx</option>
                  <option value="UPS">UPS</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Environment
                <select
                  className="border border-slate-300 px-2 py-2"
                  value={form.environment}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, environment: e.target.value as 'SANDBOX' | 'PRODUCTION' }))
                  }
                >
                  <option value="SANDBOX">Sandbox</option>
                  <option value="PRODUCTION">Production</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm md:col-span-2">
                Label
                <input className="border border-slate-300 px-2 py-2" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
              </label>
              <label className="flex flex-col gap-1 text-sm md:col-span-2">
                Client ID
                <input className="border border-slate-300 px-2 py-2" value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))} />
              </label>
              <label className="flex flex-col gap-1 text-sm md:col-span-2">
                Client secret
                <input type="password" className="border border-slate-300 px-2 py-2" value={form.clientSecret} onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                {form.provider === 'UPS'
                  ? t('backoffice.carrier_accounts_ups_shipper', {
                      defaultValue: 'UPS shipper account number (required)',
                    })
                  : t('backoffice.carrier_accounts_fedex_account', {
                      defaultValue: 'FedEx account number',
                    })}
                <input
                  className="border border-slate-300 px-2 py-2"
                  value={form.provider === 'UPS' ? form.shipperNumber : form.accountNumber}
                  onChange={(e) =>
                    setForm((f) =>
                      f.provider === 'UPS'
                        ? { ...f, shipperNumber: e.target.value }
                        : { ...f, accountNumber: e.target.value },
                    )
                  }
                />
              </label>
              {form.provider === 'FEDEX' ? (
                <label className="flex flex-col gap-1 text-sm">
                  {t('backoffice.carrier_accounts_ups_shipper_optional', {
                    defaultValue: 'Shipper number (optional)',
                  })}
                  <input
                    className="border border-slate-300 px-2 py-2"
                    value={form.shipperNumber}
                    onChange={(e) => setForm((f) => ({ ...f, shipperNumber: e.target.value }))}
                  />
                </label>
              ) : (
                <label className="flex flex-col gap-1 text-sm">
                  {t('backoffice.carrier_accounts_fedex_account_optional', {
                    defaultValue: 'FedEx account (optional reference)',
                  })}
                  <input
                    className="border border-slate-300 px-2 py-2"
                    value={form.accountNumber}
                    onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
                  />
                </label>
              )}
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}
            </button>
          </section>
        ) : null}
      </main>
    </MainLayout>
  );
}
